'use client';

import { useRouter } from 'next/navigation';
import { useMenuItemContext } from '../menu-item-context';
import { Button, Counter } from '@instamenu/ui-system-next';
import { Gift } from 'lucide-react';
import { formatCurrency } from '@instamenu/formatters';
import { findInvalidModifierGroups, useCheckoutContext } from '@instamenu/context-checkout';
import { captureClientException } from '@instamenu/posthog/client';
import { themeVars } from '@instamenu/ui-vanilla-extract';
import { modalButtonDiv } from './menu-item-details.css';
import { useState } from 'react';
import { observer } from 'mobx-react-lite';

export type AddToCartButtonProps = {
  returnPath: string;
};

export const AddToCartButton = observer(({ returnPath }: AddToCartButtonProps) => {
  const checkout = useCheckoutContext();
  const ctx = useMenuItemContext();
  const router = useRouter();
  const [max] = useState(
    checkout && ctx?.item && typeof ctx.item.count === 'number'
      ? ctx.item.count -
          checkout.numInCart(ctx.item.id) +
          (ctx.orderItem ? ctx.orderItem.quantity : 0)
      : undefined,
  );

  if (!ctx) return null;

  const {
    item,
    price,
    modifiers,
    quantity,
    instructions,
    setQuantity,
    setModifierGroupErrors,
    orderItem,
  } = ctx;

  if (!checkout?.order) return null;

  const handleAddItem = () => {
    const modifierErrors = findInvalidModifierGroups(item, modifiers);
    if (modifierErrors?.length) {
      setModifierGroupErrors(modifierErrors);
      return;
    }

    setModifierGroupErrors([]);

    try {
      if (!checkout.order.id) {
        checkout.createOrder({ item, quantity, modifiers, instructions });
      } else if (!orderItem) {
        checkout.order.addOrderItem({ item, quantity, modifiers, instructions });
      } else {
        orderItem.update({ quantity, modifiers, instructions });
      }
      router.push(returnPath, { scroll: false });
    } catch (error) {
      captureClientException(error, {
        source: 'ui-storefront.menu-item.add-to-cart',
        properties: {
          itemId: item.id,
          orderItemId: orderItem?.id,
          quantity,
        },
      });
    }
  };
  const handleSetQuantity = (q: number) => {
    setQuantity(q);
  };

  const numInCart = checkout.numInCart(item.id);
  const canAcceptOrders = checkout.isAcceptingInstantOrders || checkout.isAcceptingScheduledOrders;
  const disabled =
    !canAcceptOrders ||
    item.outOfStock ||
    !item.isAvailable ||
    (!orderItem && typeof item.count === 'number' && numInCart >= item.count);

  return (
    <div className={modalButtonDiv}>
      {orderItem?.loyaltyComped ? (
        <Gift size={22} color={themeVars.color.brand} />
      ) : (
        <Counter value={quantity} min={1} max={max} onChange={(q) => handleSetQuantity(q)} />
      )}
      <Button
        data-id="add-to-cart"
        onClick={handleAddItem}
        disabled={disabled}
        loading={checkout.order.loading}
      >
        {!canAcceptOrders
          ? 'Not accepting online orders'
          : item.outOfStock
            ? 'Out of Stock'
            : !item.isAvailable
              ? 'Currently unavailable'
              : orderItem
                ? `Update Item - ${formatCurrency(
                    orderItem.loyaltyComped
                      ? orderItem.loyaltyCompedModifiers
                        ? 0
                        : modifiers.reduce((sum, m) => sum + (m.price || 0), 0) * quantity
                      : price,
                  )}`
                : `Add Item - ${formatCurrency(price)}`}
      </Button>
    </div>
  );
});
